import React from 'react'
import { useQuery } from '@apollo/client'
import { Routes, Route, useParams } from 'react-router-dom'
import VariableCreditLineOffer from '../VariableCreditLineOffer'
import StandardCreditLineOffer from '../StandardCreditLineOffer'
import SuccessPreApprovedDualOffer from '../SuccessPreApprovedDualOffer'
import SuccessFrozenCredit from '../SuccessFrozenCredit'
import SuccessWorkingOutDetails from '../SuccessWorkingOutDetails'
import useDivvyUUID from '../../hooks/useDivvyUUID'
import GetDecisions from '../../pages/gql/GetDecisions.gql'
import GetApplicationStatuses from '../Dashboard/gql/GetApplicationStatuses.gql'
import GetDesiredCreditLimit from '../../pages/gql/GetDesiredCreditLimit.gql'
import RequireAuth from '../../components/Routes/RequireAuth'
import ApplicationDeclined from '../ApplicationDeclined/'
import AcceptOffer from '../AcceptOffer/AcceptOffer'
import { DecisionContext } from './DecisionsContext'

const ApplicationProcessed = () => {
  const { appId } = useParams()
  const [companyId] = useDivvyUUID()
  const { data: decisionsData } = useQuery(GetDecisions, {
    fetchPolicy: 'network-only',
    skip: !companyId,
    variables: { companyId },
  })
  const { data: applicationData } = useQuery(GetApplicationStatuses)

  const { data: financeData } = useQuery(GetDesiredCreditLimit, {
    skip: !appId,
    variables: { creditApplicationId: appId },
  })

  const desiredCredit = financeData?.creditApplication?.financeInfo?.desiredCredit

  const decisions = decisionsData?.decisions || []
  const variableOfferOnly = decisions?.length === 1 && decisions?.[0]?.underwritingMethod === 'auto_cash_underwriting'
  const filteredStandardOffer = decisions.filter((decision) => decision.underwritingMethod !== 'auto_cash_underwriting')
  const filteredVariableOffer = decisions.filter((decision) => decision.underwritingMethod === 'auto_cash_underwriting')
  const decisionId = decisions?.[0]?.id

  const applicationCount = applicationData?.creditApplications?.length
  const hasApplications = applicationCount > 0

  const standardOfferApprovedAmount = filteredStandardOffer?.[0]?.approvedAmountInCents
  const variableOfferApprovedAmount = filteredVariableOffer?.[0]?.approvedAmountInCents
  const approvedAmount = decisions?.[0]?.approvedAmountInCents

  return (
    <>
      <DecisionContext.Provider value={{ decisionId, decisions }}>
        <Routes>
          <Route
            element={
              <RequireAuth>
                <SuccessPreApprovedDualOffer
                  desiredCredit={desiredCredit}
                  filteredStandardOffer={filteredStandardOffer}
                  filteredVariableOffer={filteredVariableOffer}
                />
              </RequireAuth>
            }
            path='/dual-offer'
          />
          <Route
            element={
              <RequireAuth>
                <StandardCreditLineOffer desiredCredit={desiredCredit} />
              </RequireAuth>
            }
            path='/dual-offer/standard-pre-approved'
          />
          <Route
            element={
              <RequireAuth>
                <VariableCreditLineOffer desiredCredit={desiredCredit} />
              </RequireAuth>
            }
            path='/dual-offer/variable-pre-approved'
          />
          <Route
            element={
              <RequireAuth>
                {!variableOfferOnly && <StandardCreditLineOffer desiredCredit={desiredCredit} />}
                {variableOfferOnly && <VariableCreditLineOffer desiredCredit={desiredCredit} />}
              </RequireAuth>
            }
            path='/pre-approved'
          />

          <Route
            exact
            element={<AcceptOffer approvedAmount={approvedAmount} />}
            path='/pre-approved/next-steps'
          />
          <Route
            exact
            element={<AcceptOffer approvedAmount={standardOfferApprovedAmount} />}
            path='dual-offer/standard-pre-approved/next-steps'
          />
          <Route
            exact
            element={<AcceptOffer approvedAmount={variableOfferApprovedAmount} />}
            path='dual-offer/variable-pre-approved/next-steps'
          />
          <Route
            element={<RequireAuth component={SuccessFrozenCredit} />}
            path='/frozen-credit'
          />
          <Route
            element={<RequireAuth component={SuccessWorkingOutDetails} />}
            path='/working-out-details'
          />
          <Route
            element={
              <RequireAuth>
                <ApplicationDeclined hasApplications={hasApplications} />
              </RequireAuth>
            }
            path='/declined'
          />
        </Routes>
      </DecisionContext.Provider>
    </>
  )
}

export default ApplicationProcessed
